import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import { Link } from "react-router-dom";
import "./MatchList.css";

// Import avatars
import Avatar1 from "../components/avatars/avatar1";
import Avatar2 from "../components/avatars/avatar2";
import Avatar3 from "../components/avatars/avatar3";
import Avatar4 from "../components/avatars/avatar4";
import Avatar5 from "../components/avatars/avatar5";
import Avatar6 from "../components/avatars/avatar6";
import Avatar7 from "../components/avatars/avatar7";
import Avatar8 from "../components/avatars/avatar8";
import Avatar9 from "../components/avatars/avatar9";
import AvatarDefault from "../components/avatars/AvatarDefault";

const avatarMap = {
  default: AvatarDefault,
  avatar1: Avatar1,
  avatar2: Avatar2,
  avatar3: Avatar3,
  avatar4: Avatar4,
  avatar5: Avatar5,
  avatar6: Avatar6,
  avatar7: Avatar7,
  avatar8: Avatar8,
  avatar9: Avatar9,
};

const MatchList = () => {
  const { user } = useContext(UserContext);
  const [matches, setMatches] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [expandedMatchIds, setExpandedMatchIds] = useState([]);

  useEffect(() => {
    const userId = user?.id || user?._id;
    if (!userId) return;

    const fetchMatches = async () => {
      try {
        const res = await axios.get(
          `https://bookmatchup.onrender.com/api/matches?userId=${userId}&limit=50`
        );
        const sortedMatches = res.data.sort((a, b) => b.score - a.score);
        setMatches(sortedMatches);
      } catch (err) {
        console.error("Error fetching matches:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [user]);

  const avatarMemo = useMemo(() => {
    const map = {};
    matches.forEach((match) => {
      map[match.userId] = avatarMap[match.avatar] || AvatarDefault;
    });
    return map;
  }, [matches]);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 5);
  };

  const toggleExpand = (matchId) => {
    setExpandedMatchIds((prev) =>
      prev.includes(matchId)
        ? prev.filter((id) => id !== matchId)
        : [...prev, matchId]
    );
  };

  const isExpanded = (matchId) => expandedMatchIds.includes(matchId);

  if (loading) {
    return (
      <div className="custom-loading">
        <img
          src="/images/logo-spinner.png"
          alt="Loading..."
          className="rotating-logo"
        />
        <p className="loading-text">Loading matches...</p>
      </div>
    );
  }

  if (!Array.isArray(matches)) return <p>No match data yet.</p>;

  return (
    <div className="match-list-container">
      <h3 className="match-list-title">Top Reader Matches</h3>
      <p className="match-list-description">
        Click on a match's name to explore their public profile and find your
        next read.
      </p>

      {matches.length === 0 ? (
        <p>No matches found yet.</p>
      ) : (
        <ul className="match-list">
          {matches.slice(0, visibleCount).map((match) => {
            const AvatarComponent = avatarMemo[match.userId] || AvatarDefault;

            return (
              <li key={match.userId} className="match-card">
                <div className="match-header">
                  <div
                    className="match-avatar"
                    style={{ borderColor: match.avatarColor || "#ccc" }}
                  >
                    <AvatarComponent
                      color={match.avatarColor || "#222"}
                      size={55}
                    />
                  </div>
                  <strong className="match-username">
                    <Link to={`/public-profile/${match.userId}`}>
                      {match.username}
                    </Link>
                  </strong>
                </div>

                <p className="match-score">Match Score: {match.score}</p>

                <ul className="insight-list">
                  {(isExpanded(match.userId)
                    ? match.insights
                    : match.insights.slice(0, 2)
                  ).map((insight, idx) => (
                    <li key={idx} className="insight-item">
                      {insight.cover_url ? (
                        <Link
                          to={`/book/${encodeURIComponent(
                            insight.itemId || insight.bookId
                          )}`}
                        >
                          <img
                            src={insight.cover_url}
                            alt={insight.title || "Book cover"}
                            className="insight-cover"
                          />
                        </Link>
                      ) : (
                        <div className="insight-cover-placeholder" />
                      )}
                      <div className="insight-text">
                        <strong>
                          <Link
                            to={`/book/${encodeURIComponent(
                              insight.itemId || insight.bookId
                            )}`}
                          >
                            {insight.title || "Untitled"}
                          </Link>
                        </strong>
                        <br />
                        {formatInsight(insight)}
                      </div>
                    </li>
                  ))}
                </ul>

                {match.insights.length > 2 && (
                  <button
                    className="expand-button"
                    onClick={() => toggleExpand(match.userId)}
                  >
                    {isExpanded(match.userId) ? "Show Less" : "Show More"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {visibleCount < matches.length && (
        <button className="show-more-button" onClick={handleShowMore}>
          Show More Matches
        </button>
      )}
    </div>
  );
};

function formatInsight(insight) {
  const typeMap = {
    completed: "You both completed this book",
    bothReviewed: "You both reviewed this book",
    both5Star: "You both rated it 5 stars",
    foundHelpful: "They found your review helpful",
    bothWishlisted: "You both wishlisted this book",
    wishlistCompletedOverlap:
      "One of you wishlisted it, the other completed it",
  };
  return typeMap[insight.type] || "Something in common on this book";
}

export default MatchList;
